import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface INavigationContext {
  popUntil: (targetRoute: string) => void;
  navigateTo: (
    currentRoute: string,
    destinationRoute: string,
    replace?: boolean,
    check?: boolean
  ) => void;
  navigateOrPop: (route: string) => boolean;
  historyStack: string[];
}

export const NavigationContext = createContext<INavigationContext | null>(null);

const useNavigationProvider = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [historyStack, setHistoryStack] = useState<string[]>(() => {
    const storedHistory = sessionStorage.getItem("historyStack");
    return storedHistory ? JSON.parse(storedHistory) : [];
  });

  useEffect(() => {
    sessionStorage.setItem("historyStack", JSON.stringify(historyStack));
  }, [historyStack]);

  useEffect(() => {
    setHistoryStack((prev) => {
      if (prev[prev.length - 1] !== location.pathname) {
        const newStack = [...prev, location.pathname];
        sessionStorage.setItem("historyStack", JSON.stringify(newStack));
        return newStack;
      }
      return prev;
    });
  }, [location.pathname]);

  const popUntil = (targetRoute: string) => {
    const index = historyStack.lastIndexOf(targetRoute);

    if (index !== -1) {
      const stepsBack = historyStack.length - 1 - index;
      const newStack = historyStack.slice(0, index + 1);
      setHistoryStack(newStack);
      sessionStorage.setItem("historyStack", JSON.stringify(newStack));
      navigate(-stepsBack);
    } else {
      navigate(targetRoute, { replace: true });
    }
  };

  const navigateTo = (
    currentRoute: string,
    destinationRoute: string,
    replace = false,
    check = false
  ) => {
    if (check && currentRoute !== location.pathname) {
      console.warn(
        `Navigation prevented: currentRoute (${currentRoute}) does not match browser route (${location.pathname})`
      );
      return;
    }

    const index = historyStack.lastIndexOf(destinationRoute);
    if (index !== -1) {
      popUntil(destinationRoute);
      return;
    }

    setHistoryStack((prev) => {
      const newStack = replace
        ? [...prev.slice(0, -1), destinationRoute]
        : [...prev, destinationRoute];
      sessionStorage.setItem("historyStack", JSON.stringify(newStack));
      return newStack;
    });

    navigate(destinationRoute, { replace });
  };

  const navigateOrPop = (route: string): boolean => {
    const index = historyStack.lastIndexOf(route);

    if (index !== -1) {
      popUntil(route);
      return true;
    } else {
      setHistoryStack((prev) => {
        const newStack = [...prev, route];
        sessionStorage.setItem("historyStack", JSON.stringify(newStack));
        return newStack;
      });
      navigate(route);
      return false;
    }
  };

  return { popUntil, navigateTo, navigateOrPop, historyStack };
};

interface Props {
  children: React.ReactNode;
}

export const NavigationProvider: React.FC<Props> = ({ children }) => {
  const navigation = useNavigationProvider();

  return (
    <NavigationContext.Provider value={navigation}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useAppNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      "useAppNavigation must be used within a NavigationProvider"
    );
  }
  return context;
};
